/**
 * Fuente única de verdad de los datos institucionales de Mi Lugar.
 *
 * Todo lo que se muestre en pantalla o se emita como JSON-LD tiene que salir de
 * acá. El motivo es concreto: cuando un mismo dato aparece en dos lugares con
 * valores distintos (metadatos vs. texto visible), los asistentes de IA que leen
 * el sitio sintetizan las dos fuentes y terminan afirmando cosas que la
 * institución no sostiene.
 */

export const SITE_URL = 'https://www.cetmilugar.com';

// @id estables para que el grafo de schema.org enlace entidades entre sí.
export const ORG_ID = `${SITE_URL}/#organizacion`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

export const ORGANIZACION = {
  nombre: 'Mi Lugar — Centro Educativo Terapéutico y Centro de Día',
  nombreCorto: 'Mi Lugar',
  alternateName: ['CET Mi Lugar', 'Centro de Día Mi Lugar', 'CD Mi Lugar', 'Mi Lugar Temperley'],
  descripcion:
    'Centro Educativo Terapéutico (CET) y Centro de Día para personas con discapacidad ' +
    'intelectual en la zona sur del Gran Buenos Aires. Dos sedes: Temperley y Glew.',
  fundacion: '1982',
  fundadora: 'Lic. Mirtha Wechsung',
  email: 'tulugar1@hotmail.com',
  logo: `${SITE_URL}/images/Logo.png`,
  horario: 'Mo-Fr 08:00-16:00',
  horarioTexto: 'Lunes a viernes de 8 a 16 hs',
  redes: [
    'https://www.facebook.com/MiLugarTemperley',
    'https://www.instagram.com/cet.milugar/',
  ],
};

/** Años de trayectoria calculados, para no dejar "+40 años" clavado en el texto. */
export const aniosDeTrayectoria = (hoy = new Date()) =>
  hoy.getFullYear() - Number(ORGANIZACION.fundacion);

export const SEDES = [
  {
    slug: 'temperley',
    id: `${SITE_URL}/temperley#sede`,
    nombre: 'Mi Lugar Temperley — CET y Centro de Día',
    nombreCorto: 'Sede Temperley',
    calle: 'A. Gallardo 646',
    localidad: 'Temperley',
    provincia: 'Buenos Aires',
    codigoPostal: '1834',
    pais: 'AR',
    direccionCompleta: 'A. Gallardo 646, Temperley, Provincia de Buenos Aires',
    lat: -34.7667,
    lng: -58.3833,
    telefono: '+5491165651675',
    telefonoTexto: '11 6565-1675',
    whatsapp: 'https://wa.me/5491165651675',
    imagen: `${SITE_URL}/images/temperley.jpg`,
    zonas: ['Temperley', 'Lomas de Zamora', 'Banfield', 'Lanús', 'Adrogué', 'Turdera'],
  },
  {
    slug: 'glew',
    id: `${SITE_URL}/glew#sede`,
    nombre: 'Mi Lugar Glew — CET y Centro de Día',
    nombreCorto: 'Sede Glew',
    calle: 'Av. Hipólito Yrigoyen 22548',
    // La sede se conoce como "Glew" pero la dirección postal está en Guernica.
    // Ambos nombres tienen que aparecer explícitos o quien busque por uno solo no llega.
    localidad: 'Guernica',
    provincia: 'Buenos Aires',
    codigoPostal: 'B1856',
    pais: 'AR',
    direccionCompleta: 'Av. Hipólito Yrigoyen 22548, B1856 Guernica, Provincia de Buenos Aires',
    lat: -34.90664,
    lng: -58.39074,
    telefono: '+5491165661677',
    telefonoTexto: '11 6566-1677',
    whatsapp: 'https://wa.me/5491165661677',
    imagen: `${SITE_URL}/images/glew.png`,
    zonas: ['Glew', 'Guernica', 'Longchamps', 'Burzaco', 'Alejandro Korn', 'San Vicente'],
  },
];

export const sedePorSlug = (slug) => SEDES.find((s) => s.slug === slug);

/** Todas las localidades cubiertas, sin repetir. */
export const ZONAS_ATENDIDAS = [...new Set(SEDES.flatMap((s) => s.zonas))];

/**
 * Cobertura. `noAtendidas` es tan importante como el resto: declarar el negativo
 * de forma explícita es lo que evita que un asistente infiera cobertura a partir
 * de frases generales.
 */
export const OBRAS_SOCIALES = {
  principales: [
    { nombre: 'UPCN', cobertura: '100% en prestaciones básicas' },
    { nombre: 'UOCRA', cobertura: '100% en prestaciones básicas' },
    { nombre: 'OSDE', cobertura: '100% en prestaciones básicas' },
    { nombre: 'Swiss Medical', cobertura: '100% en prestaciones básicas' },
    { nombre: 'PROFE', cobertura: '100% en prestaciones básicas' },
    { nombre: 'Obra Social Ciudad de Buenos Aires', cobertura: '100% en prestaciones básicas' },
  ],
  adicionales: [
    'OSECAC', 'OSPACA', 'OSUTHGRA', 'OSPACP',
    'OSPEDYC', 'OSPJN', 'OSPLAD', 'OSPATCA', 'OSCHOCA',
  ],
  noAtendidas: ['PAMI', 'IOMA'],
};

export const nombresObrasSociales = () => [
  ...OBRAS_SOCIALES.principales.map((os) => os.nombre),
  ...OBRAS_SOCIALES.adicionales,
];

export const SERVICIOS = {
  cet: {
    slug: 'cet',
    nombre: 'Centro Educativo Terapéutico (CET)',
    descripcion:
      'Educación especial integrada con terapias individuales para niños y jóvenes ' +
      'con discapacidad intelectual y trastornos del desarrollo.',
    edades: 'de 6 a 35 años',
    edadMinima: 6,
    edadMaxima: 35,
  },
  cd: {
    slug: 'cd',
    nombre: 'Centro de Día (CD)',
    descripcion:
      'Atención integral diurna para jóvenes y adultos con discapacidad intelectual, ' +
      'con jornada simple o completa de 8 a 16 hs.',
    edades: 'jóvenes y adultos a partir de los 6 años',
    edadMinima: 6,
  },
};

export const CONDICIONES_ATENDIDAS = [
  'Discapacidad intelectual leve, moderada o severa',
  'Trastornos del espectro autista (TEA)',
  'Síndrome de Down',
  'Parálisis cerebral',
  'Trastornos generalizados del desarrollo',
  'Multidiscapacidad',
  'Retraso madurativo',
];

export const TERAPIAS = [
  { nombre: 'Psicología', descripcion: 'Atención psicológica especializada para personas con discapacidad' },
  { nombre: 'Fonoaudiología', descripcion: 'Terapia del lenguaje y comunicación' },
  { nombre: 'Terapia Ocupacional', descripcion: 'Desarrollo de habilidades para la vida diaria' },
  { nombre: 'Kinesiología', descripcion: 'Rehabilitación motriz y trabajo corporal' },
  { nombre: 'Musicoterapia', descripcion: 'Terapia a través de la música' },
  { nombre: 'Hidroterapia', descripcion: 'Terapia acuática especializada (sede Temperley)' },
];
